import { Tag, Loader2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

/**
 * Cart summary for the customer checkout. Tax and the total are the SERVER's:
 * shown when the server priced the cart (a promo check answers its pricing),
 * else "Calculated at checkout" - the payment screen shows the server's total.
 */
export default function OrderSummaryCard({
  items,
  subtotal,
  discount = 0,
  appliedPromo = null,
  taxRatePercent = null,
  tax,
  tip,
  total,
  promoCode,
  setPromoCode,
  onApplyPromo,
  onRemovePromo,
  promoChecking = false,
  onPay,
  isProcessing,
  disabled = false
}) {
  return (
    <Card className="sticky top-24">
      <CardHeader>
        <CardTitle className="text-lg">Order Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {item.quantity}x {item.name}
              </span>
              <span>${Number(item.total_price || 0).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <Separator />

        {appliedPromo ? (
          <div className="flex items-center justify-between text-sm" data-testid="applied-promo">
            <span className="flex items-center gap-2 text-green-700">
              <Tag className="h-4 w-4" /> {appliedPromo.code}
            </span>
            <Button variant="ghost" size="sm" onClick={onRemovePromo} data-testid="remove-promo">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="Promo code"
              className="flex-1"
              maxLength={50}
              data-testid="promo-code"
            />
            <Button variant="outline" onClick={onApplyPromo} disabled={promoChecking || !promoCode} data-testid="apply-promo">
              {promoChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />}
            </Button>
          </div>
        )}

        <Separator />

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Promo Discount</span>
              <span>-${discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground whitespace-nowrap">
              Tax{taxRatePercent !== null && taxRatePercent !== undefined ? ` (${taxRatePercent}%)` : ''}
            </span>
            {tax == null
              ? <span className="text-muted-foreground text-right" data-testid="tax-at-checkout">Calculated at checkout</span>
              : <span>${tax.toFixed(2)}</span>}
          </div>
          {tip > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tip</span>
              <span>${tip.toFixed(2)}</span>
            </div>
          )}
        </div>

        <Separator />

        {total != null ? (
          <div className="flex justify-between font-bold text-lg">
            <span>Total</span>
            <span data-testid="checkout-total">${total.toFixed(2)}</span>
          </div>
        ) : (
          <div className="flex justify-between font-semibold">
            <span>Subtotal{tip > 0 ? ' + tip' : ''}</span>
            <span>${(subtotal - discount + tip).toFixed(2)} <span className="text-xs font-normal text-muted-foreground">+ tax</span></span>
          </div>
        )}

        <Button
          onClick={onPay}
          disabled={isProcessing || disabled}
          className="w-full btn-pill bg-primary hover:bg-primary/90"
          data-testid="place-order-btn"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Placing order...
            </>
          ) : (
            total != null ? `Place Order & Pay $${total.toFixed(2)}` : 'Place Order & Pay'
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          You pay on the restaurant's secure Aster Gateway page, which shows the exact total.
        </p>
      </CardContent>
    </Card>
  );
}
